import React from "react";
import { Box, Container, VStack, Grid, Heading, Text, HStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Hero from "../components/common/Hero";
import Card from "../components/common/Card";
import DisciplineCard from "../components/common/DisciplineCard";
import CTAButton from "../components/common/CTAButton";
import { disciplines } from "../data/disciplines";
import {
  FaRocket,
  FaChartLine,
  FaBriefcase,
  FaUsers,
  FaCertificate,
  FaGraduationCap,
} from "react-icons/fa";

const MotionBox = motion(Box);

export default function Home() {
  const strengths = [
    {
      icon: FaRocket,
      title: "Industry-Aligned Curriculum",
      description: "Learn exactly what companies are hiring for, not outdated textbooks.",
    },
    {
      icon: FaChartLine,
      title: "Proven Outcomes",
      description: "85%+ placement rate within 3 months with competitive salary packages.",
    },
    {
      icon: FaBriefcase,
      title: "Real Projects & Internships",
      description: "Build a portfolio of real work. Work on actual industry problems.",
    },
    {
      icon: FaUsers,
      title: "Mentorship & Community",
      description: "Learn from industry professionals. Grow with peer cohorts.",
    },
    {
      icon: FaCertificate,
      title: "Certifications & Proof",
      description: "Industry-recognized certifications + verifiable project portfolio.",
    },
    {
      icon: FaGraduationCap,
      title: "Continuous Learning",
      description: "Skills that stay relevant for 10+ years, not just today.",
    },
  ];

  return (
    <Box>
      <Navbar />

      {/* Hero Section */}
      <Hero
        title="Industry-Ready Careers for College Students"
        subtitle="Bridge the gap between academic learning and industry expectations. Get hired for roles you can actually perform well."
        badge="College Enablement Program"
        withGradient
      >
        <HStack spacing={4} justify="center" flexWrap="wrap">
          <CTAButton to="/for-colleges" size="lg">
            Partner With Your College
          </CTAButton>
          <CTAButton to="/for-students" variant="outline" size="lg">
            Explore as a Student
          </CTAButton>
        </HStack>
      </Hero>

      {/* Why Ojasa Mirai - 6 Strength Cards */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            {/* Section Header */}
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#0F172A">
                Why Choose Ojasa Mirai for College Enablement?
              </Heading>
              <Text fontSize="lg" color="#1E293B" maxW="2xl">
                We're not just another training provider. We're your partner in creating industry-ready graduates.
              </Text>
            </VStack>

            {/* 6 Cards Grid */}
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap={8}
              w="full"
            >
              {strengths.map((item, idx) => (
                <Card
                  key={idx}
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                />
              ))}
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Who This Is For Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#0F172A">
                For Everyone in Your College Ecosystem
              </Heading>
              <Text fontSize="lg" color="#64748B" maxW="2xl">
                Ojasa Mirai works for students, faculty, and college leadership. Here's how.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8} w="full">
              {/* For Students */}
              <Card
                icon={FaGraduationCap}
                title="For Students"
                description="Get industry-ready skills, build your portfolio, ace interviews, and land jobs you'll excel at. Not just placements - meaningful careers."
              />

              {/* For Faculty */}
              <Card
                icon={FaUsers}
                title="For Faculty"
                description="Collaborate, don't compete. Get co-teaching support, upskilling opportunities, and see your students succeed."
              />

              {/* For Colleges */}
              <Card
                icon={FaBriefcase}
                title="For Recruitment Cells & Management"
                description="Improve placement outcomes, enhance college reputation, get real-time metrics, and build industry partnerships."
              />
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Disciplines Preview */}
      <Box bg="#F8FAFC" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#0F172A">
                6 Industry-Aligned Disciplines
              </Heading>
              <Text fontSize="lg" color="#64748B" maxW="2xl">
                Choose the path that matches your career goals. Each discipline is designed for specific market demands.
              </Text>
            </VStack>

            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }}
              gap={8}
              w="full"
            >
              {disciplines.map((disc) => (
                <DisciplineCard
                  key={disc.id}
                  id={disc.id}
                  name={disc.name}
                  description={disc.description}
                  icon={disc.icon}
                />
              ))}
            </Grid>

            <CTAButton to="/disciplines" size="lg" mt={4}>
              Explore All Disciplines
            </CTAButton>
          </VStack>
        </Container>
      </Box>

      {/* Program Outcomes Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="2xl" color="#0F172A">
                Three Pathways to Success
              </Heading>
              <Text fontSize="lg" color="#64748B" maxW="2xl">
                Every student graduates with one or more of these outcomes.
              </Text>
            </VStack>

            <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={8} w="full">
              {/* Employability */}
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card
                  icon={FaBriefcase}
                  title="Placement & Employability"
                  description="85%+ placement in 3 months. Land roles you're ready for. Competitive salary packages. Real job skills."
                />
              </MotionBox>

              {/* Internships */}
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <Card
                  icon={FaRocket}
                  title="Internships & Industry Exposure"
                  description="2-4 week micro-internships with real startups and SMEs. Build your network. Get performance-based recommendations."
                />
              </MotionBox>

              {/* Entrepreneurship */}
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <Card
                  icon={FaChartLine}
                  title="Entrepreneurship & Startup Mindset"
                  description="Build products, launch ventures, raise funding. We support founders through the entire journey."
                />
              </MotionBox>
            </Grid>
          </VStack>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box
        bgGradient="linear(to-br, #0F172A, #1E293B, #10B981)"
        py={{ base: 12, md: 20 }}
        color="white"
      >
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h2" size="2xl" color="white">
              Ready to Transform College Outcomes?
            </Heading>
            <Text fontSize="lg" maxW="2xl" color="#FFFFFF">
              Whether you're a student looking to get industry-ready or a college wanting to improve placements, we're here to help.
            </Text>

            <HStack spacing={4} justify="center" flexWrap="wrap">
              <CTAButton to="/for-colleges">
                Partner With Us
              </CTAButton>
              <CTAButton to="/contact" variant="outline">
                Schedule a Discussion
              </CTAButton>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
